import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { CodeBlock } from '../components/CodeBlock';
import { InfoBox } from '../components/InfoBox';
export function ApiTransactionPage({
  onNavigate
}: {
  onNavigate?: (id: string) => void;
}) {
  return <DocPageLayout title="Transaction API" description="Operations related to processing payments, including authorization, capture, refund, and void." breadcrumbs={[{
    label: 'API Reference',
    id: 'api-overview'
  }, {
    label: 'Transaction'
  }]} onNavigate={onNavigate} sections={[{
    id: 'pay',
    title: 'Process Payment (PAY)'
  }, {
    id: 'authorize',
    title: 'Authorize Payment'
  }, {
    id: 'capture',
    title: 'Capture Payment'
  }, {
    id: 'refund',
    title: 'Refund Payment'
  }, {
    id: 'void',
    title: 'Void Transaction'
  }, {
    id: 'verify',
    title: 'Verify Card'
  }, {
    id: 'retrieve',
    title: 'Retrieve Order'
  }]} data-id="element-928">
      <p className="text-gray-700 mb-8" data-id="element-929">
        The Transaction API is the core of the payment gateway, allowing you to
        process payments, manage funds, and handle the lifecycle of an order.
      </p>

      <h2 id="pay" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-930">
        Process a Payment (PAY)
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-931">
        The{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-932">
          PAY
        </code>{' '}
        operation performs both authorization and capture in a single step. This
        is the most common operation for immediate purchases.
      </p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-933">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-934">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-935">/merchant/</span>
        <span className="text-blue-600" data-id="element-936">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-937">/order/</span>
        <span className="text-blue-600" data-id="element-938">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-939">/transaction/</span>
        <span className="text-blue-600" data-id="element-940">{'{transactionId}'}</span>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mt-8 mb-4" data-id="element-941">
        Request Parameters
      </h3>
      <div className="overflow-x-auto mb-8" data-id="element-942">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-943">
          <thead data-id="element-944">
            <tr className="bg-gray-100" data-id="element-945">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b w-1/3" data-id="element-946">
                Field
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b w-1/6" data-id="element-947">
                Type
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b w-1/2" data-id="element-948">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-949">
            <tr data-id="element-950">
              <td className="px-4 py-3 font-mono text-xs font-semibold" data-id="element-951">
                apiOperation <span className="text-red-500" data-id="element-952">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-953">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-954">
                Must be set to{' '}
                <code className="bg-gray-100 px-1 rounded" data-id="element-955">PAY</code>.
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-956">
              <td className="px-4 py-3 font-mono text-xs font-semibold" data-id="element-957">
                order.amount <span className="text-red-500" data-id="element-958">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-959">Decimal</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-960">
                The total amount to charge.
              </td>
            </tr>
            <tr data-id="element-961">
              <td className="px-4 py-3 font-mono text-xs font-semibold" data-id="element-962">
                order.currency <span className="text-red-500" data-id="element-963">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-964">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-965">
                3-character ISO 4217 currency code.
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-966">
              <td className="px-4 py-3 font-mono text-xs font-semibold" data-id="element-967">
                sourceOfFunds.type <span className="text-red-500" data-id="element-968">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-969">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-970">
                The payment source type (e.g.,{' '}
                <code className="bg-gray-100 px-1 rounded" data-id="element-971">CARD</code>,{' '}
                <code className="bg-gray-100 px-1 rounded" data-id="element-972">TOKEN</code>).
              </td>
            </tr>
            <tr data-id="element-973">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-974">
                sourceOfFunds.provided.card.number
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-975">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-976">
                Required if type is CARD. The card PAN.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <CodeBlock language="JSON" title="PAY Request" code={`{
  "apiOperation": "PAY",
  "order": {
    "amount": "50.00",
    "currency": "USD"
  },
  "sourceOfFunds": {
    "type": "CARD",
    "provided": {
      "card": {
        "number": "5123450000000008",
        "securityCode": "123",
        "expiry": { "month": "12", "year": "25" }
      }
    }
  }
}`} data-id="element-977" />

      <InfoBox type="tip" title="Idempotency" data-id="element-978">
        The combination of{' '}
        <code className="bg-green-100 px-1 rounded text-xs font-mono" data-id="element-979">
          orderId
        </code>{' '}
        and{' '}
        <code className="bg-green-100 px-1 rounded text-xs font-mono" data-id="element-980">
          transactionId
        </code>{' '}
        makes the request idempotent. If you resend the exact same request, the
        gateway will return the original response without charging the card
        again.
      </InfoBox>

      <h2 id="authorize" className="text-2xl font-bold text-gray-900 mt-12 mb-4 border-b border-gray-200 pb-2" data-id="element-981">
        Authorize a Payment (AUTHORIZE)
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-982">
        The{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-983">
          AUTHORIZE
        </code>{' '}
        operation reserves funds on the cardholder's account but does not
        transfer them. You must follow up with a{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-984">
          CAPTURE
        </code>{' '}
        operation to collect the funds.
      </p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-985">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-986">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-987">/merchant/</span>
        <span className="text-blue-600" data-id="element-988">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-989">/order/</span>
        <span className="text-blue-600" data-id="element-990">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-991">/transaction/</span>
        <span className="text-blue-600" data-id="element-992">{'{transactionId}'}</span>
      </div>
      <CodeBlock language="JSON" title="AUTHORIZE Request" code={`{
  "apiOperation": "AUTHORIZE",
  "order": {
    "amount": "50.00",
    "currency": "USD"
  },
  "sourceOfFunds": {
    "type": "CARD",
    "provided": {
      "card": {
        "number": "5123450000000008",
        "securityCode": "123",
        "expiry": { "month": "12", "year": "25" }
      }
    }
  }
}`} data-id="element-993" />

      <h2 id="capture" className="text-2xl font-bold text-gray-900 mt-12 mb-4 border-b border-gray-200 pb-2" data-id="element-994">
        Capture a Payment (CAPTURE)
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-995">
        The{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-996">
          CAPTURE
        </code>{' '}
        operation transfers funds that were previously authorized. You can
        capture the full amount or a partial amount.
      </p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-997">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-998">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-999">/merchant/</span>
        <span className="text-blue-600" data-id="element-1000">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-1001">/order/</span>
        <span className="text-blue-600" data-id="element-1002">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-1003">/transaction/</span>
        <span className="text-blue-600" data-id="element-1004">{'{transactionId}'}</span>
      </div>
      <CodeBlock language="JSON" title="CAPTURE Request" code={`{
  "apiOperation": "CAPTURE",
  "transaction": {
    "amount": "50.00",
    "currency": "USD"
  }
}`} data-id="element-1005" />

      <h2 id="refund" className="text-2xl font-bold text-gray-900 mt-12 mb-4 border-b border-gray-200 pb-2" data-id="element-1006">
        Refund a Payment (REFUND)
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1007">
        The{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1008">
          REFUND
        </code>{' '}
        operation returns funds to the cardholder for a previously captured
        transaction. You can refund the full amount or a partial amount.
      </p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-1009">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-1010">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-1011">/merchant/</span>
        <span className="text-blue-600" data-id="element-1012">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-1013">/order/</span>
        <span className="text-blue-600" data-id="element-1014">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-1015">/transaction/</span>
        <span className="text-blue-600" data-id="element-1016">{'{transactionId}'}</span>
      </div>
      <CodeBlock language="JSON" title="REFUND Request" code={`{
  "apiOperation": "REFUND",
  "transaction": {
    "amount": "25.00",
    "currency": "USD"
  }
}`} data-id="element-1017" />

      <h2 id="void" className="text-2xl font-bold text-gray-900 mt-12 mb-4 border-b border-gray-200 pb-2" data-id="element-1018">
        Void a Transaction (VOID)
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1019">
        The{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1020">
          VOID
        </code>{' '}
        operation cancels a previously authorized transaction before it has been
        captured. This releases the hold on the cardholder's funds.
      </p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-1021">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-1022">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-1023">/merchant/</span>
        <span className="text-blue-600" data-id="element-1024">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-1025">/order/</span>
        <span className="text-blue-600" data-id="element-1026">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-1027">/transaction/</span>
        <span className="text-blue-600" data-id="element-1028">{'{transactionId}'}</span>
      </div>
      <CodeBlock language="JSON" title="VOID Request" code={`{
  "apiOperation": "VOID",
  "transaction": {
    "targetTransactionId": "TXN-001"
  }
}`} data-id="element-1029" />

      <h2 id="verify" className="text-2xl font-bold text-gray-900 mt-12 mb-4 border-b border-gray-200 pb-2" data-id="element-1030">
        Verify a Card (VERIFY)
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1031">
        The{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1032">
          VERIFY
        </code>{' '}
        operation validates a card without charging it. This is useful for
        card-on-file scenarios where you want to verify the card before storing
        it.
      </p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-1033">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-1034">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-1035">/merchant/</span>
        <span className="text-blue-600" data-id="element-1036">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-1037">/order/</span>
        <span className="text-blue-600" data-id="element-1038">{'{orderId}'}</span>
        <span className="text-gray-600" data-id="element-1039">/transaction/</span>
        <span className="text-blue-600" data-id="element-1040">{'{transactionId}'}</span>
      </div>
      <CodeBlock language="JSON" title="VERIFY Request" code={`{
  "apiOperation": "VERIFY",
  "order": {
    "currency": "USD"
  },
  "sourceOfFunds": {
    "type": "CARD",
    "provided": {
      "card": {
        "number": "5123450000000008",
        "securityCode": "123",
        "expiry": { "month": "12", "year": "25" }
      }
    }
  }
}`} data-id="element-1041" />

      <h2 id="retrieve" className="text-2xl font-bold text-gray-900 mt-12 mb-4 border-b border-gray-200 pb-2" data-id="element-1042">
        Retrieve Order
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1043">
        Retrieve the details of an order including all transactions associated
        with it.
      </p>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-1044">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 mr-2" data-id="element-1045">
          GET
        </span>
        <span className="text-gray-600" data-id="element-1046">/merchant/</span>
        <span className="text-blue-600" data-id="element-1047">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-1048">/order/</span>
        <span className="text-blue-600" data-id="element-1049">{'{orderId}'}</span>
      </div>
      <CodeBlock language="JSON" title="Retrieve Order Response" code={`{
  "result": "SUCCESS",
  "order": {
    "id": "ORD-001",
    "amount": "50.00",
    "currency": "USD",
    "status": "CAPTURED",
    "totalAuthorizedAmount": "50.00",
    "totalCapturedAmount": "50.00",
    "totalRefundedAmount": "0.00"
  },
  "transaction": [
    {
      "id": "TXN-001",
      "type": "AUTHORIZATION",
      "result": "SUCCESS",
      "amount": "50.00"
    },
    {
      "id": "TXN-002",
      "type": "CAPTURE",
      "result": "SUCCESS",
      "amount": "50.00"
    }
  ]
}`} data-id="element-1050" />
    </DocPageLayout>;
}